import { decodificarXml, detectarExcepcion } from './gml';

/**
 * Cliente del WFS 2.0 INSPIRE de la Direccion General del Catastro.
 * Limite conocido: 1 km2 por peticion (responde 200 con ows:ExceptionReport si se pasa),
 * de ahi el teselado de `geo/teselado.ts`. CORS: permite cualquier origen (comprobado 2026-09-22).
 */
export const WFS_CP = 'https://ovc.catastro.meh.es/INSPIRE/wfsCP.aspx';
export const WFS_BU = 'https://ovc.catastro.meh.es/INSPIRE/wfsBU.aspx';
export const WFS_AD = 'https://ovc.catastro.meh.es/INSPIRE/wfsAD.aspx';

export type CapaCatastro = 'cp' | 'bu' | 'bp';

export const NOMBRE_CAPA: Record<CapaCatastro, string> = {
  cp: 'Parcelas',
  bu: 'Edificios',
  bp: 'Partes de edificio',
};

const TYPENAME: Record<CapaCatastro, string> = {
  cp: 'cp.cadastralparcel',
  bu: 'BU.BUILDING',
  bp: 'BU.BUILDINGPART',
};

function base(capa: CapaCatastro): string {
  return capa === 'cp' ? WFS_CP : WFS_BU;
}

/** GetFeature por caja; `bbox` es "minx,miny,maxx,maxy" en el huso indicado. */
export function urlGetFeature(capa: CapaCatastro, bbox: string, epsg: number): string {
  return `${base(capa)}?service=wfs&version=2&request=getfeature&typenames=${TYPENAME[capa]}`
    + `&srsname=${encodeURIComponent(`EPSG::${epsg}`)}&bbox=${encodeURIComponent(bbox)}`;
}

/** Consultas almacenadas por referencia catastral (14 caracteres). */
export function urlParcelaPorRc(rc: string, epsg: number): string {
  return `${WFS_CP}?service=wfs&version=2&request=GetFeature&STOREDQUERIE_ID=GetParcel&refcat=${encodeURIComponent(rc)}&srsname=${encodeURIComponent(`EPSG::${epsg}`)}`;
}

export function urlEdificioPorRc(rc: string, epsg: number): string {
  return `${WFS_BU}?service=wfs&version=2&request=GetFeature&STOREDQUERIE_ID=GetBuildingByParcel&refcat=${encodeURIComponent(rc)}&srsname=${encodeURIComponent(`EPSG::${epsg}`)}`;
}

export function urlDireccionesPorRc(rc: string, epsg: number): string {
  return `${WFS_AD}?service=wfs&version=2&request=GetFeature&STOREDQUERIE_ID=GetadByRefcat&refcat=${encodeURIComponent(rc)}&srsname=${encodeURIComponent(`EPSG::${epsg}`)}`;
}

export class ErrorWfs extends Error {
  constructor(mensaje: string, public readonly url: string, public readonly estado?: number) {
    super(mensaje);
    this.name = 'ErrorWfs';
  }
}

export interface OpcionesDescarga {
  reintentos?: number;
  timeoutMs?: number;
  f?: typeof fetch;
  userAgent?: string;
}

function esperar(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Descarga un GML con reintentos (espera creciente) y decodificacion segun la declaracion XML.
 * Una respuesta HTTP correcta que contenga un ExceptionReport se trata como error y no se reintenta
 * (es un error de la peticion, no de la red).
 */
export async function descargarGml(url: string, op: OpcionesDescarga = {}): Promise<string> {
  const reintentos = op.reintentos ?? 3;
  const timeoutMs = op.timeoutMs ?? 90_000;
  const f = op.f ?? fetch;
  let ultimo: unknown = null;
  for (let intento = 0; intento <= reintentos; intento++) {
    const ctrl = new AbortController();
    const temporizador = setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const resp = await f(url, { signal: ctrl.signal, headers: { Accept: 'text/xml, application/gml+xml' } });
      if (!resp.ok) {
        ultimo = new ErrorWfs(`HTTP ${resp.status} ${resp.statusText}`, url, resp.status);
        if (resp.status >= 400 && resp.status < 500 && resp.status !== 429) throw ultimo;
      } else {
        const xml = decodificarXml(await resp.arrayBuffer());
        const excepcion = detectarExcepcion(xml);
        if (excepcion) throw new ErrorWfs(excepcion, url, resp.status);
        return xml;
      }
    } catch (e) {
      if (e instanceof ErrorWfs && !(e.estado === 429 || (e.estado ?? 0) >= 500)) throw e;
      ultimo = e;
    } finally {
      clearTimeout(temporizador);
    }
    if (intento < reintentos) await esperar(1000 * 2 ** intento);
  }
  const msg = ultimo instanceof Error ? ultimo.message : String(ultimo);
  throw new ErrorWfs(`No se pudo descargar tras ${reintentos + 1} intentos: ${msg}`, url);
}
